import type { ReactNode } from "react";
import {
  BookOpen,
  BookMarked,
  LibraryBig,
  FileText,
  Blocks as BlocksIcon,
  ImageIcon,
  NotebookPen,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BlocksPanel } from "../builder/BlocksPanel";
import { PlaceholderPanel } from "./PlaceholderPanel";
import { WorkspaceMediaPanel } from "../draft/WorkspaceMediaPicker";
import { NotesPanel } from "@/components/notes/NotesPanel";
import { useWorkspace } from "@/hooks/useWorkspace";
import { tierEntitlements } from "@/lib/tier";

const MediaRailPanel = () => {
  const { active } = useWorkspace();
  if (!active) {
    return (
      <PlaceholderPanel
        title="Workspace Media"
        bundle="H3"
        description="Select a workspace to browse and drag in stored images."
      />
    );
  }
  const capBytes = tierEntitlements(active.account_tier).workspaceMediaBytes;
  return (
    <div className="h-full overflow-auto pr-1">
      <p className="mb-2 text-xs text-muted-foreground">
        Drag any image into the Draft Canvas to insert it. No re-upload.
      </p>
      <WorkspaceMediaPanel workspaceId={active.id} capBytes={capBytes} managerOnly />
    </div>
  );
};

const NotesRailPanel = () => {
  const { active } = useWorkspace();
  if (!active) {
    return (
      <PlaceholderPanel
        title="Notes"
        bundle="H3"
        description="Select a workspace to capture quick notes."
      />
    );
  }
  return (
    <div className="h-full overflow-auto pr-1">
      <NotesPanel workspaceId={active.id} />
    </div>
  );
};

export const RightRailContent = ({
  draftsPanel,
  libraryPanel,
  excerptsPanel,
  bibliographyPanel,
}: {
  draftsPanel?: ReactNode;
  /** @deprecated Outbox lives on /publisher; kept for callsite compat. */
  outboxPanel?: ReactNode;
  libraryPanel?: ReactNode;
  excerptsPanel?: ReactNode;
  bibliographyPanel?: ReactNode;
}) => (
  <div className="flex h-full flex-col p-3">
    <Tabs defaultValue="drafts" className="flex h-full flex-col">
      <TabsList className="grid w-full grid-cols-7">
        <TabsTrigger value="drafts" className="px-1.5" aria-label="Drafts">
          <FileText className="h-3.5 w-3.5" />
        </TabsTrigger>
        <TabsTrigger value="blocks" className="px-1.5" aria-label="Blocks">
          <BlocksIcon className="h-3.5 w-3.5" />
        </TabsTrigger>
        <TabsTrigger value="excerpts" className="px-1.5" aria-label="My Excerpts">
          <BookOpen className="h-3.5 w-3.5" />
        </TabsTrigger>
        <TabsTrigger value="library" className="px-1.5" aria-label="My Library">
          <LibraryBig className="h-3.5 w-3.5" />
        </TabsTrigger>
        <TabsTrigger value="notes" className="px-1.5" aria-label="Notes">
          <NotebookPen className="h-3.5 w-3.5" />
        </TabsTrigger>
        <TabsTrigger value="bibliography" className="px-1.5" aria-label="Bibliography">
          <BookMarked className="h-3.5 w-3.5" />
        </TabsTrigger>
        <TabsTrigger value="media" className="px-1.5" aria-label="Workspace Media">
          <ImageIcon className="h-3.5 w-3.5" />
        </TabsTrigger>
      </TabsList>
      <div className="mt-3 flex-1 overflow-auto">
        <TabsContent value="drafts" className="mt-0 h-full">
          {draftsPanel ?? (
            <PlaceholderPanel
              title="Drafts queue"
              bundle="H3"
              description="Your editable drafts across pieces. Click one to load it into the canvas."
            />
          )}
        </TabsContent>
        <TabsContent value="blocks" className="mt-0 h-full">
          <BlocksPanel mode="drag" />
        </TabsContent>
        <TabsContent value="excerpts" className="mt-0 h-full">
          {excerptsPanel ?? (
            <PlaceholderPanel
              title="My Excerpts"
              bundle="H3"
              description="Search your saved excerpts; drag-to-insert into the active draft."
            />
          )}
        </TabsContent>
        <TabsContent value="library" className="mt-0 h-full">
          {libraryPanel ?? (
            <PlaceholderPanel
              title="My Library"
              bundle="H3"
              description="Search your saved books, articles, and sources to attach as references."
            />
          )}
        </TabsContent>
        <TabsContent value="notes" className="mt-0 h-full">
          <NotesRailPanel />
        </TabsContent>
        <TabsContent value="bibliography" className="mt-0 h-full">
          {bibliographyPanel ?? (
            <PlaceholderPanel
              title="Bibliography"
              bundle="H3"
              description="Open a piece to review and curate its bibliography entries."
            />
          )}
        </TabsContent>
        <TabsContent value="media" className="mt-0 h-full">
          <MediaRailPanel />
        </TabsContent>
      </div>
    </Tabs>
  </div>
);
